import {
  RestartAlt as RestartAltIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Payment as PaymentIcon,
  Pending as PendingIcon,
  HourglassTop as HourglassTopIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import {
  isInvoiceActionsMutationsLoadingAtom,
  isReceivePaymentFormActiveAtom,
  toastOptionsAtom,
  userAtom,
} from "../../../atoms";
import {
  DefaultActionMenuItem,
  InvoiceStatusTargetActions,
} from "../../../types";
import { useEffect, useRef, useState } from "react";
import { InvoiceStatus, Role } from "../../../enums";
import { useAtom } from "jotai";
import { DefaultActionMenu } from "../../common";
import { useBulkUpdateInvoiceStatusMutation } from "../../../api/invoiceService";
import { useSendInvoiceToQuickbooksMutation } from "../../../api";

type InvoiceActionsProps = {
  status: InvoiceStatus;
  invoiceNumbers: string[];
  orgId: number;
  accountId: number;
  invoiceStatusTargetActions: InvoiceStatusTargetActions;
  disabled?: boolean;
  hasQuickbooksId?: boolean;
  handleRefresh?: () => void;
};

export const InvoiceActions = ({
  status,
  invoiceNumbers,
  orgId,
  accountId,
  invoiceStatusTargetActions,
  disabled,
  handleRefresh,
  hasQuickbooksId,
}: InvoiceActionsProps) => {
  const [user] = useAtom(userAtom);
  const invoiceNumbersRef = useRef(invoiceNumbers);
  const [invoiceActions, setInvoiceActions] = useState<DefaultActionMenuItem[]>(
    [],
  );
  const [, setIsReceivePaymentFormActive] = useAtom(
    isReceivePaymentFormActiveAtom,
  );
  const [, setIsInvoiceActionsMutationsLoading] = useAtom(
    isInvoiceActionsMutationsLoadingAtom,
  );
  const [, setToastOptions] = useAtom(toastOptionsAtom);
  const bulkUpdateInvoiceStatusMutation = useBulkUpdateInvoiceStatusMutation();
  const sendInvoiceToQuickbooksMutation = useSendInvoiceToQuickbooksMutation();

  const handleStatusUpdate = (status: InvoiceStatus) => {
    bulkUpdateInvoiceStatusMutation.mutate({
      accountId,
      orgId,
      invoiceNumbers: invoiceNumbersRef.current,
      invoiceStatus: status,
    });
  };

  const handleSendInvoiceToQuickbooks = () => {
    sendInvoiceToQuickbooksMutation.mutate({
      orgId,
      accountId,
      invoiceNumber: invoiceNumbers[0],
    });
  };

  const allInvoiceActions: {
    [key in InvoiceStatus]?: DefaultActionMenuItem;
  } = {
    [InvoiceStatus.Cancelled]: {
      label: "Cancel Invoice",
      icon: CancelIcon,
      action: () => {
        handleStatusUpdate(InvoiceStatus.Cancelled);
      },
    },
    [InvoiceStatus.OnHold]: {
      label: "Hold Invoice",
      icon: HourglassTopIcon,
      action: () => {
        handleStatusUpdate(InvoiceStatus.OnHold);
      },
    },
    [InvoiceStatus.Pending]: {
      label: "To Pending",
      icon: PendingIcon,
      action: () => {
        handleStatusUpdate(InvoiceStatus.Pending);
      },
    },
    [InvoiceStatus.InProgress]: {
      label: "To Progress",
      icon: RestartAltIcon,
      action: () => {
        handleStatusUpdate(InvoiceStatus.InProgress);
      },
    },
    [InvoiceStatus.PaymentReceived]: {
      label:
        user?.roleCode === Role.Customer ? "Make Payment" : "Process Payment",
      icon: PaymentIcon,
      color: status === InvoiceStatus.SendToCustomer ? "success" : "primary",
      action: () => {
        setIsReceivePaymentFormActive(true);
      },
    },
    [InvoiceStatus.SendToCustomer]: {
      label: "To Customer",
      icon: SendIcon,
      action: () => {
        handleStatusUpdate(InvoiceStatus.SendToCustomer);
      },
    },
    [InvoiceStatus.SendToQuickbooks]: {
      label: "Send to Quickbooks",
      icon: SendIcon,
      action: () => {
        handleSendInvoiceToQuickbooks();
      },
    },
    [InvoiceStatus.Deleted]: {
      label: "Delete Invoice",
      icon: DeleteIcon,
      action: () => {
        handleStatusUpdate(InvoiceStatus.Deleted);
      },
    },
  };

  useEffect(() => {
    setIsReceivePaymentFormActive(false);
  }, []);

  useEffect(() => {
    setInvoiceActions(getInvoiceActions(status, invoiceNumbers));
  }, [status, invoiceNumbers]);

  useEffect(() => {
    invoiceNumbersRef.current = invoiceNumbers;
  }, [invoiceNumbers]);

  useEffect(() => {
    if (bulkUpdateInvoiceStatusMutation.isSuccess) {
      setToastOptions({
        open: true,
        message: "Status updated successfully",
        severity: "success",
      });
      handleRefresh && handleRefresh();
    }

    if (bulkUpdateInvoiceStatusMutation.isError) {
      setToastOptions({
        open: true,
        message: "Something went wrong",
        severity: "error",
      });
    }
  }, [
    bulkUpdateInvoiceStatusMutation.isLoading,
    bulkUpdateInvoiceStatusMutation.isSuccess,
    bulkUpdateInvoiceStatusMutation.isError,
  ]);

  useEffect(() => {
    if (sendInvoiceToQuickbooksMutation.isSuccess) {
      setToastOptions({
        open: true,
        message: "Invoice sent to quickbooks",
        severity: "success",
      });
      handleRefresh && handleRefresh();
    }

    if (sendInvoiceToQuickbooksMutation.isError) {
      // TODO: show proper message. (Missing token, missing credentials, try connecting to qb)
      setToastOptions({
        open: true,
        message: "Something went wrong",
        severity: "error",
      });
    }
  }, [
    sendInvoiceToQuickbooksMutation.isLoading,
    sendInvoiceToQuickbooksMutation.isSuccess,
    sendInvoiceToQuickbooksMutation.isError,
  ]);

  useEffect(() => {
    setIsInvoiceActionsMutationsLoading(
      bulkUpdateInvoiceStatusMutation.isLoading ||
        sendInvoiceToQuickbooksMutation.isLoading,
    );
  }, [
    bulkUpdateInvoiceStatusMutation.isLoading,
    sendInvoiceToQuickbooksMutation.isLoading,
  ]);

  const getInvoiceActions = (
    status: InvoiceStatus,
    invoiceNumbers: string[],
  ): DefaultActionMenuItem[] => {
    const makeDefault = (
      item?: DefaultActionMenuItem,
    ): DefaultActionMenuItem | undefined => {
      return item
        ? {
            default: true,
            ...item,
          }
        : item;
    };

    if (
      !Object.keys(invoiceStatusTargetActions).includes(status) ||
      !invoiceStatusTargetActions[status]
    ) {
      return [];
    }
    let { defaultAction, targetActions } = invoiceStatusTargetActions[status];
    if (
      defaultAction === InvoiceStatus.PaymentReceived &&
      invoiceNumbers.length > 1 &&
      targetActions
    ) {
      defaultAction = targetActions[0];
      targetActions = targetActions.slice(1);
    }

    let invoiceActions = [
      makeDefault(allInvoiceActions[defaultAction]),
      invoiceNumbers.length === 1 && status === InvoiceStatus.All
        ? allInvoiceActions[InvoiceStatus.SendToQuickbooks]
        : undefined,
      ...(targetActions || []).map((key) => allInvoiceActions[key]),
    ].filter((e) => e !== undefined);

    if (!hasQuickbooksId) {
      invoiceActions = invoiceActions.filter(
        (invoiceAction) =>
          invoiceAction.label !==
          allInvoiceActions[InvoiceStatus.SendToQuickbooks]?.label,
      );
    }

    if (
      !invoiceActions.some((item) => item.default) &&
      invoiceActions.length > 0
    ) {
      invoiceActions[0] = makeDefault(
        invoiceActions[0],
      ) as DefaultActionMenuItem;
    }

    return invoiceActions;
  };

  return <DefaultActionMenu disabled={disabled} menu={invoiceActions} />;
};
